import { FC } from "react";
import { Accordion, Checkbox, Stack, Text } from "@mantine/core";

import { generatePathIgnoreOptions } from "@shared/utils";

export interface IgnoreFutureFindingsOptionsProps {
  ignoreOption?: string;
  setIgnoreOption: (ignoreOption?: string) => void;
  disableRule: boolean;
  setDisableRule: (disableRule: boolean) => void;
  findingPath: string;
  showIgnoreFilesOption?: boolean;
  showDisableRuleOption?: boolean;
}

/**
 *
 * @param ignoreOption the ignore option that the user has selected
 * @param setIgnoreOption function to set the ignore option
 * @param disableRule whether the user has selected to disable the rule
 * @param setDisableRule function to set whether the user has selected to disable the rule
 * @param findingPath the full path of the finding, for use when displaying path ignore options to the user
 * @param findingRepo the repo the finding is in, because in order to add to the .semgrepignore, we need to know
 *              what repo this finding is in. If undefined, won't show the .semgrepignore section
 * @param findingRuleBoard the rule board that contains the rule that found the finding. If the user chooses to disable
 *                the rule so it doesn't find any more findings, we need to know what policy to remove it from.
 * @returns options for ignoring a file and disabling a rule in future scans. Parent controls ignoreOption and
 *          disableRule state
 */
export const IgnoreFutureFindingsOptions: FC<
  IgnoreFutureFindingsOptionsProps
> = ({
  ignoreOption,
  setIgnoreOption,
  disableRule,
  setDisableRule,
  findingPath,
  showIgnoreFilesOption,
  showDisableRuleOption,
}) => {
  const ignoreOptions = generatePathIgnoreOptions(findingPath);

  const handleIgnoreOptionChange = (values: string[]) => {
    if (values.length === 0) {
      setIgnoreOption(undefined);
    } else {
      // can only select one option at a time
      setIgnoreOption(values.find((val) => val !== ignoreOption));
    }
  };

  if (!showIgnoreFilesOption && !showDisableRuleOption) {
    return null;
  }

  return (
    <Accordion
      multiple
      defaultValue={[]}
      variant="filled"
      chevronPosition="left"
      styles={(theme) => ({
        label: { fontSize: theme.fontSizes.xs },
        item: { backgroundColor: "inherit !important" },
        chevron: { marginRight: 4 },
      })}
    >
      {showIgnoreFilesOption && (
        <Accordion.Item value="ignore" data-testid="accordion-ignore-files">
          <Accordion.Control px={0}>
            Ignore files in future scans...
          </Accordion.Control>
          <Accordion.Panel>
            <Checkbox.Group
              value={ignoreOption ? [ignoreOption as string] : []}
              onChange={(value) => handleIgnoreOptionChange(value)}
              size="xs"
              data-testid="ignore-files-checkbox-group"
              error={
                ignoreOption ? (
                  <Text inherit pt="xs">
                    This path will be added to your .semgrepignore. Future
                    Semgrep scans will ignore all findings from all rules within
                    this path.
                  </Text>
                ) : undefined
              }
            >
              <Stack gap="xs">
                {Object.entries(ignoreOptions).map(
                  ([optionVal, optionInfo]) => (
                    <Checkbox
                      key={optionVal}
                      value={optionVal}
                      label={optionInfo.label}
                    />
                  )
                )}
              </Stack>
            </Checkbox.Group>
          </Accordion.Panel>
        </Accordion.Item>
      )}
      {showDisableRuleOption && (
        <Accordion.Item value="disable" data-testid="accordion-disable-rule">
          <Accordion.Control px={0}>Disable rule...</Accordion.Control>
          <Accordion.Panel>
            <Checkbox.Group
              value={disableRule ? ["disable"] : []}
              onChange={(value) => setDisableRule(value.length > 0)}
              size="xs"
            >
              <Checkbox value="disable" label={`Disable from policy`} />
            </Checkbox.Group>
          </Accordion.Panel>
        </Accordion.Item>
      )}
    </Accordion>
  );
};
